// API + types for the Interviewer Prep surface. Mirrors the backend InterviewPrepGuide /
// InterviewPrepMessage shape served at /api_ats/v1/ats_interviews/:id/prep_guide|prep_messages.

import { http } from '@/lib/ats-https';

export type PrepStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type Priority = 'must_cover' | 'core' | 'optional';
export type GroundedIn =
  | 'scorecard_question'
  | 'job_description'
  | 'candidate_background'
  | 'prior_round_gap';

export interface FocusArea {
  area: string;
  grounded_in: GroundedIn;
  // Verbatim source text that grounds this area (the scorecard question, JD line, etc.) — shown on hover.
  source_ref: string;
  why: string;
  priority: Priority;
  suggested_owner: string;
}
export interface Suggestion {
  // Short 2-5 word title for the round-at-a-glance legend; the full detail is in `text`.
  label: string;
  text: string;
  sample_question: string;
  depth: string;
  mins: number;
}
export interface Callout {
  label: string;
  body: string;
}
export interface PrepGuideSections {
  about_candidate: string;
  previous_rounds_context: string;
  focus_areas: FocusArea[];
  focus_areas_to_skip: string[];
  suggestions: Suggestion[];
  callouts: Callout[];
}
export interface PrepEditor {
  id: number;
  name: string;
  avatar_url: string | null;
}
export interface PrepGuide {
  id: number;
  status: PrepStatus;
  sections: PrepGuideSections | null;
  last_edited_at: string | null;
  last_edited_by: PrepEditor | null;
  created_at: string;
  updated_at: string;
}
export interface PrepMessage {
  id: number;
  status: PrepStatus;
  query: string;
  response: string | null;
  progress?: string | null;
  // The staged "Apply to prep" change summary, when this turn proposed one.
  proposes_prep_change?: string | null;
  // Whether that staged change has already been applied (persisted, so a reopened page reflects it).
  prep_change_applied?: boolean;
  // Up to four contextual follow-up questions the copilot suggested for this turn (FE pills).
  followups?: string[];
  created_at: string;
}

type InterviewId = number | string;

export const getPrepGuide = async (
  interviewId: InterviewId
): Promise<PrepGuide> => {
  // Cache-bust: the guide is polled while generating/applying and mutates underneath us, so a
  // browser-cached GET would make the poll read stale data and never see the edit land.
  const res = await http.get(`/ats_interviews/${interviewId}/prep_guide`, {
    params: { _t: Date.now() },
  });
  return res.data.prep_guide;
};

export const listPrepMessages = async (
  interviewId: InterviewId
): Promise<PrepMessage[]> => {
  const res = await http.get(`/ats_interviews/${interviewId}/prep_messages`);
  return res.data.prep_messages;
};

export const sendPrepMessage = async (
  interviewId: InterviewId,
  query: string
): Promise<PrepMessage> => {
  const res = await http.post(`/ats_interviews/${interviewId}/prep_messages`, {
    query,
  });
  return res.data.prep_message;
};

export const getPrepMessage = async (
  interviewId: InterviewId,
  id: number
): Promise<PrepMessage> => {
  const res = await http.get(
    `/ats_interviews/${interviewId}/prep_messages/${id}`
  );
  return res.data.prep_message;
};

export const retryPrepMessage = async (
  interviewId: InterviewId,
  id: number
): Promise<PrepMessage> => {
  const res = await http.post(
    `/ats_interviews/${interviewId}/prep_messages/${id}/retry`
  );
  return res.data.prep_message;
};

// Apply the change the interviewer agreed in `messageId`. Async on the server (an
// LLM edit), so the caller polls the guide until last_edited_at advances.
export const applyPrepEdit = async (
  interviewId: InterviewId,
  messageId: number
): Promise<void> => {
  await http.post(`/ats_interviews/${interviewId}/prep_guide/apply`, {
    message_id: messageId,
  });
};

// The highlight-to-ask passage rides inside the query as a leading Markdown blockquote — the
// backend reads it natively and it renders as a quote in the bubble. Pure formatting helper.
export const composeQuery = (text: string, context?: string | null): string => {
  const body = text.trim() || 'Tell me more about this.';
  if (!context) return body;
  const quoted = context.trim().replace(/\n+/g, ' ');
  return `> ${quoted}\n\n${body}`;
};

// Inverse of composeQuery for display: split a stored query back into its highlighted context
// (the leading blockquote, if any) and the typed text.
export const splitQuery = (
  query: string
): { context?: string; text: string } => {
  const match = query.match(/^>\s?(.+?)\n\n([\s\S]*)$/);
  if (match) return { context: match[1].trim(), text: match[2].trim() };
  return { text: query };
};

export const isTerminal = (status?: PrepStatus): boolean =>
  status === 'completed' || status === 'failed';

export const formatMins = (mins: number): string => `~${mins} min`;
